import type { PersonItem } from './person_row.js';
import type { TravelerSearchState } from './traveler_picker.js';
import {
  toPerson,
  useTravelerSearch,
  withPhotos,
  type TravelerDirectory,
} from './use_traveler_search.js';

/** Um viajante como o pedido o guarda: `{id, name}`. */
type TravelerRef = { readonly id: string; readonly name: string };

/** O ciclo da busca de viajantes, controlado pelo host (o roster da empresa
 *  pagina e busca no servidor). */
export type TravelerFieldSearch = {
  readonly items: readonly PersonItem[];
  /** O solicitante, fixo no topo ("Você"). */
  readonly pinned?: PersonItem;
  readonly query: string;
  readonly state: TravelerSearchState;
  readonly hasMore: boolean;
  /** Falso quando o host não tem busca: a lista é o próprio valor. */
  readonly searchable: boolean;
  readonly onQueryChange: (query: string) => void;
  readonly onLoadMore?: () => void;
};

/**
 * A busca de um campo QUEM (ônibus, carro) sobre a busca de viajantes do kit
 * (`useTravelerSearch`, a mesma do aéreo): o solicitante fixo no topo ("Você")
 * e o roster paginado do
 * `directory` do host. Sem `directory`, a lista é o próprio valor. Os cards da
 * conversa (genui-kit) e as páginas do web-chat montam o campo por aqui.
 */
export function useTravelerFieldSearch({
  live,
  directory,
  requester,
  travelers,
}: {
  /** Busca só enquanto o campo pode ser editado (o card congelado não busca). */
  readonly live: boolean;
  readonly directory: TravelerDirectory | undefined;
  readonly requester: TravelerRef | undefined;
  readonly travelers: readonly TravelerRef[];
}): { search: TravelerFieldSearch; photos: ReadonlyMap<string, string> } {
  const viewerPhotoUrl = directory?.viewerPhotoUrl;
  const search = useTravelerSearch(
    live,
    directory?.searchTravelers,
    requester && viewerPhotoUrl !== undefined
      ? { id: requester.id, photoUrl: viewerPhotoUrl }
      : undefined,
  );
  const searchable = directory?.searchTravelers !== undefined;
  const [pinned]: (PersonItem | undefined)[] = requester
    ? withPhotos([requester], search.photos)
    : [];
  return {
    photos: search.photos,
    search: {
      items: searchable ? search.results.map(toPerson) : withPhotos(travelers, search.photos),
      ...(pinned ? { pinned } : {}),
      query: search.term,
      state: searchable ? search.state : 'ready',
      hasMore: search.hasNext,
      searchable,
      onQueryChange: search.setTerm,
      onLoadMore: search.loadMore,
    },
  };
}
